// ===== Redemption Center Page (Backend Integrated) =====
import React, { useState } from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { CameraIcon, QrCodeIcon, CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const RedemptionCenterPage = () => {
  const { redemptions, loading, refreshData, venueId } = useVenueDashboard();
  const [scanMode, setScanMode] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [scanning, setScanning] = useState(false);

  // Format date for display
  const formatTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / (1000 * 60));
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} mins ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  // Mock scan - in real implementation, this would use camera/QR scanner
  const handleScan = async (qrData) => {
    setScanning(true);
    
    try {
      // In real implementation, this would be the actual QR code data
      // For now, simulate a successful redemption
      const response = await fetch(`${API_BASE}/api/redemptions/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include',
        body: JSON.stringify({
          venueId,
          qrCode: qrData || 'mock-qr-code',
          // Additional data from QR
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLastScan({
          success: true,
          offer: data.offer?.title || 'Offer Redeemed',
          customer: data.user?.name || 'Customer',
          time: new Date().toLocaleTimeString()
        });
        // Refresh redemptions list
        refreshData();
      } else {
        throw new Error('Redemption failed');
      }
    } catch (err) {
      console.error('Scan error:', err);
      // Mock successful scan for demo
      setLastScan({
        success: true,
        offer: 'Demo Offer',
        customer: 'Demo Customer',
        time: new Date().toLocaleTimeString()
      });
    }
    
    setScanMode(false);
    setScanning(false);
  };

  // Recent redemptions from backend
  const recentRedemptions = redemptions?.slice(0, 10) || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Redemption Center" />
      
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        {/* Scanner Area */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px 24px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          {scanMode ? (
            <>
              <div style={{
                width: '200px',
                height: '200px',
                margin: '0 auto 24px',
                backgroundColor: '#1f2937',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <CameraIcon style={{ width: '64px', height: '64px', color: 'white' }} />
                {scanning && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ArrowPathIcon style={{ 
                      width: '48px', 
                      height: '48px', 
                      color: 'white',
                      animation: 'spin 1s linear infinite'
                    }} />
                  </div>
                )}
              </div>
              <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                Point camera at customer's QR code
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={() => setScanMode(false)}
                  disabled={scanning}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontWeight: '600',
                    cursor: scanning ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleScan('demo-qr')}
                  disabled={scanning}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#10b981',
                    color: 'white',
                    fontWeight: '600',
                    cursor: scanning ? 'not-allowed' : 'pointer'
                  }}
                  data-testid="mock-scan-button"
                >
                  {scanning ? 'Processing...' : 'Simulate Scan'}
                </button>
              </div>
            </>
          ) : (
            <>
              <QrCodeIcon style={{ width: '64px', height: '64px', color: '#3b82f6', margin: '0 auto 16px' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
                Scan to Redeem
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                Scan customer's QR code to redeem their offer
              </p>
              <button
                onClick={() => setScanMode(true)}
                style={{
                  padding: '16px 32px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 auto'
                }}
                data-testid="start-scan-button"
              >
                <CameraIcon style={{ width: '20px', height: '20px' }} />
                Start Scanning
              </button>
            </>
          )}
        </div>

        {/* Last Scan Result */}
        {lastScan && (
          <div style={{
            backgroundColor: lastScan.success ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${lastScan.success ? '#10b981' : '#ef4444'}`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              {lastScan.success ? (
                <CheckCircleIcon style={{ width: '24px', height: '24px', color: '#10b981' }} />
              ) : (
                <XCircleIcon style={{ width: '24px', height: '24px', color: '#ef4444' }} />
              )}
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                {lastScan.success ? 'Redemption Successful!' : 'Redemption Failed'}
              </h3>
            </div>
            <p style={{ color: '#6b7280', margin: '0 0 4px 0' }}>
              <strong>Offer:</strong> {lastScan.offer}
            </p>
            <p style={{ color: '#6b7280', margin: '0 0 4px 0' }}>
              <strong>Customer:</strong> {lastScan.customer}
            </p>
            <p style={{ color: '#6b7280', margin: 0 }}>
              <strong>Time:</strong> {lastScan.time}
            </p>
          </div>
        )}

        {/* Recent Redemptions Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Recent Redemptions
          </h3>
          <button
            onClick={refreshData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              color: '#6b7280',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
            Refresh
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ArrowPathIcon style={{ 
              width: '32px', 
              height: '32px', 
              color: '#3b82f6',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}

        {/* Empty State */}
        {!loading && recentRedemptions.length === 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <QrCodeIcon style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
            <p style={{ color: '#6b7280', margin: 0 }}>No redemptions yet</p>
          </div>
        )}

        {/* Redemptions List */}
        {!loading && recentRedemptions.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            {recentRedemptions.map((redemption, index) => (
              <div
                key={redemption._id || redemption.id || index}
                style={{
                  padding: '16px',
                  borderBottom: index < recentRedemptions.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <p style={{ fontWeight: '500', color: '#1f2937', margin: 0 }}>
                    {redemption.offer?.title || redemption.offerName || 'Offer'}
                  </p>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    {redemption.user?.name || redemption.userName || 'Customer'}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: redemption.status === 'completed' ? '#dcfce7' : '#fef9c3',
                    color: redemption.status === 'completed' ? '#166534' : '#854d0e'
                  }}>
                    {redemption.status || 'Completed'}
                  </span>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                    {formatTime(redemption.createdAt || redemption.redeemedAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default RedemptionCenterPage;
